import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useProperty } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ───────────────────────────────────────────────────
//
// The collage renders four photo tiles in a staggered two-column layout. Each
// tile is its own slot group so the field generator can pick a distinct
// outdoor photo of the property (amenity, exterior, or aerial) per tile.
// Defaults start at photo_gallery[8] to stay clear of the early gallery
// photos used by other home sections; tiles that resolve empty are filled at
// render time by `fillBlankTiles`.

const photo1Def: SlotGroupDefinition = {
  label: 'Collage Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 8,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      "Select an outdoor photo of this property — one of its outdoor amenity spaces (whichever outdoor common areas actually exist here), a building exterior, or an aerial shot of the property. Exclude off-site neighborhood shots. Never reuse an image already shown elsewhere on the home page (the hero featured image, the overview slideshow photos, the pinned-callouts background) or in another collage tile. If no unused outdoor photo is left, use an unused interior or apartment photo instead — never leave the tile empty.",
    context:
      'Tile 1 of 4 (landscape, top-left) in the home-page outdoor collage. Use the strongest outdoor photo of the property available. Must differ from tiles 2–4.',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Collage Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 9,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      "Select another outdoor photo of this property, not already used by the other tiles — an outdoor amenity space, a building exterior, or an aerial shot of the property. Exclude off-site neighborhood shots. Never reuse an image already shown elsewhere on the home page (the hero featured image, the overview slideshow photos, the pinned-callouts background) or in another collage tile. If no unused outdoor photo is left, use an unused interior or apartment photo instead — never leave the tile empty.",
    context:
      'Tile 2 of 4 (tall portrait, bottom-left) in the home-page outdoor collage. A different outdoor space or angle from tiles 1, 3, and 4.',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Collage Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[10].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[10].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 10,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      "Select an outdoor photo of this property that suits a tall portrait crop and is not used by the other tiles — an outdoor amenity space, a building exterior, or an aerial shot of the property. Exclude off-site neighborhood shots. Never reuse an image already shown elsewhere on the home page (the hero featured image, the overview slideshow photos, the pinned-callouts background) or in another collage tile. If no unused outdoor photo is left, use an unused interior or apartment photo instead — never leave the tile empty.",
    context:
      'Tile 3 of 4 (tall portrait, top-right) in the home-page outdoor collage. A different outdoor space or angle from tiles 1, 2, and 4.',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Collage Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[11].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[11].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 11,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      "Select one more outdoor photo of this property that rounds out the set — an outdoor amenity space, a building exterior, or an aerial shot of the property, showing a space or view not covered by the other tiles. Exclude off-site neighborhood shots. Never reuse an image already shown elsewhere on the home page (the hero featured image, the overview slideshow photos, the pinned-callouts background) or in another collage tile. If no unused outdoor photo is left, use an unused interior or apartment photo instead — never leave the tile empty.",
    context:
      'Tile 4 of 4 (landscape, bottom-right) in the home-page outdoor collage. A different outdoor space or angle from tiles 1–3.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Neighborhood CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: "See What's Nearby" },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const COLLAGE_SIZES = '(min-width: 768px) 25vw, 50vw';

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

interface Tile {
  image: string;
  alt: string;
}

const INTERIOR_TITLE = 'Apartment';
const OVERVIEW_SLIDESHOW_LIMIT = 8;
const PINNED_CALLOUTS_DEFAULT_INDEX = 1;

const photoSrc = (p: GalleryPhoto) => p.image || p.original_url || '';

/**
 * Fills tiles that resolved to no image with gallery photos not already shown
 * on the home page. Preference: non-interior photos, then interior photos
 * outside the overview slideshow, then any photo not in another tile — so a
 * tile is only blank when the gallery has nothing left.
 */
function fillBlankTiles(
  tiles: Tile[],
  gallery: GalleryPhoto[],
  featuredImage: string,
): Tile[] {
  const photos = gallery.filter((p) => photoSrc(p));
  const slideshow = new Set(
    photos
      .filter((p) => p.title === INTERIOR_TITLE)
      .slice(0, OVERVIEW_SLIDESHOW_LIMIT)
      .map(photoSrc),
  );
  const pageUsed = new Set<string>(slideshow);
  if (featuredImage) pageUsed.add(featuredImage);
  const pinned = gallery[PINNED_CALLOUTS_DEFAULT_INDEX];
  if (pinned && photoSrc(pinned)) pageUsed.add(photoSrc(pinned));

  const inCollage = new Set(tiles.map((t) => t.image).filter(Boolean));
  const tiers = [
    photos.filter((p) => p.title !== INTERIOR_TITLE && !pageUsed.has(photoSrc(p))),
    photos.filter((p) => p.title === INTERIOR_TITLE && !pageUsed.has(photoSrc(p))),
    photos,
  ];

  return tiles.map((tile) => {
    if (tile.image) return tile;
    for (const tier of tiers) {
      const pick = tier.find((p) => !inCollage.has(photoSrc(p)));
      if (pick) {
        inCollage.add(photoSrc(pick));
        return { image: photoSrc(pick), alt: pick.alt_text || pick.title || '' };
      }
    }
    return tile;
  });
}

export function NeighborhoodShowcase() {
  const photo1 = useSlotGroup('neighborhoodshowcase.photo1', photo1Def);
  const photo2 = useSlotGroup('neighborhoodshowcase.photo2', photo2Def);
  const photo3 = useSlotGroup('neighborhoodshowcase.photo3', photo3Def);
  const photo4 = useSlotGroup('neighborhoodshowcase.photo4', photo4Def);
  const cta = useSlotGroup('neighborhoodshowcase.cta', ctaDef);
  const property = useProperty() as Record<string, unknown>;

  const [tile1, tile2, tile3, tile4] = fillBlankTiles(
    [photo1, photo2, photo3, photo4].map((p) => ({ image: p.image || '', alt: p.alt || '' })),
    Array.isArray(property?.photo_gallery) ? (property.photo_gallery as GalleryPhoto[]) : [],
    typeof property?.property_featured_image === 'string' ? property.property_featured_image : '',
  );

  useRevealStyles();
  const imageRef = useScrollReveal<HTMLDivElement>();
  const titleRef = useScrollReveal<HTMLDivElement>();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left column — photo collage */}
          <div ref={imageRef} className="reveal-slide-right order-2 md:order-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo1" definition={photo1Def}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md shadow-md">
                    {tile1.image ? (
                      <VibesitesImage
                        url={tile1.image}
                        alt={tile1.alt}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo2" definition={photo2Def}>
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md shadow-md">
                    {tile2.image ? (
                      <VibesitesImage
                        url={tile2.image}
                        alt={tile2.alt}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
              </div>
              <div className="flex flex-col gap-4">
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo3" definition={photo3Def}>
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md shadow-md">
                    {tile3.image ? (
                      <VibesitesImage
                        url={tile3.image}
                        alt={tile3.alt}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo4" definition={photo4Def}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md shadow-md">
                    {tile4.image ? (
                      <VibesitesImage
                        url={tile4.image}
                        alt={tile4.alt}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          {/* Right column — copy */}
          <div className="order-1 md:order-2 text-center md:text-left">
            {/* Heading block — zooms from back to front */}
            <div ref={titleRef} className="reveal-zoom-front">
              <RichTextContent
                path="neighborhoodshowcase.pre_title"
                label="Subtitle"
                defaultValue="<p>Experiences Outside Your Doorstep</p>"
                as="p"
                className="font-body text-sm sm:text-base font-semibold text-[var(--vibe-color-primary)] mb-5 tracking-[0.3em] uppercase"
              />

              <RichTextContent
                path="neighborhoodshowcase.title"
                label="Title"
                defaultValue='<p>Stylish Apartments In <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
                as="h2"
                className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text)] mb-8 leading-[1.05]"
              />
            </div>

            {/* Blurb + CTA — fade up from the bottom */}
            <div ref={contentRef} className="reveal-fade-up">
              <RichTextContent
                path="neighborhoodshowcase.description"
                label="Description"
                defaultValue="<p>Your neighborhood puts parks, shopping, dining, and entertainment just minutes from your front door. Explore everything that makes this location the perfect place to call home.</p>"
                as="div"
                className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] max-w-xl mx-auto md:mx-0 leading-relaxed [&_a]:text-primary [&_a]:underline"
              />

              <EditableSlotGroup groupPath="neighborhoodshowcase.cta" definition={ctaDef}>
                <a
                  href={cta.url || '/neighborhood'}
                  target={cta._target || '_self'}
                  className="neighborhood-cta group/cta relative inline-flex items-center justify-center gap-2 mt-9 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300 max-md:w-56"
                >
                  <span className="relative z-10">{cta.label || "See What's Nearby"}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Match Header › schedule_tour_link (.nav-cta-btn): solid primary
           fill, 5px radius, surface fill + white label on hover. */
        .neighborhood-cta {
          background: var(--vibe-color-primary);
          border: 0;
          border-radius: 5px;
          color: #ffffff;
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .neighborhood-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .neighborhood-cta:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .neighborhood-cta:hover::before {
          opacity: 0;
        }
        .neighborhood-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodShowcase',
  displayName: 'Neighborhood Showcase',
  tokens: {
    'neighborhoodshowcase.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experiences Outside Your Doorstep</p>',
    },
    'neighborhoodshowcase.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Stylish Apartments In <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodshowcase.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        '<p>Your neighborhood puts parks, shopping, dining, and entertainment just minutes from your front door. Explore everything that makes this location the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'neighborhoodshowcase.photo1': photo1Def,
    'neighborhoodshowcase.photo2': photo2Def,
    'neighborhoodshowcase.photo3': photo3Def,
    'neighborhoodshowcase.photo4': photo4Def,
    'neighborhoodshowcase.cta': ctaDef,
  },
};

(NeighborhoodShowcase as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodShowcase;
